<template>
    <AddEntryToCollectionOverlay
        visible
        :collections="collections"
        :loading="isLoading"
        :selected="selected"
        @on-close="emits('onClose')"
        @on-save="onSave"
        @on-collection-selection-changed="onCollectionSelect"
        @on-collection-created="onCollectionCreated"
    >
        <template #entry>
            <div class="q-mt-md">
                <ListCard
                    :title="article.title"
                    :subtitle="article.articleId"
                    card-type="small"
                    no-hover-shadow
                    container
                    fallback-icon="fa-gear"
                />
            </div>
        </template>
    </AddEntryToCollectionOverlay>
</template>

<script setup lang="ts">
import {
    CollectionEntryMechanicNavigationContextInput,
    CollectionEntryNavigationContextInput,
    useAddArticlesToCollectionMutation,
    useAddMechanicsToCollectionMutation,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { computed, ref, toRefs, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useQuasar } from "quasar";
import { useEditableCollections } from "@/abilities/collections/composables/editable-collections.ts";
import ListCard from "@/shared/components/card/list-card.vue";
import { updateCollectionCache } from "@/abilities/collections/graphql/collection-cache.ts";
import AddEntryToCollectionOverlay from "@/abilities/collections/components/overlay/add-entry-to-collection-overlay.vue";

export interface MechanicContext {
    assemblyIdsPath: string[];
}

export interface ArticleModelOverlay {
    title: string;
    articleId?: string;
    mechanicId?: string;
    mechanicContext?: MechanicContext;
}

const { t } = useI18n();
const { notify } = useQuasar();
const selected = ref<Record<string, boolean>>({});
const saving = ref<boolean>(false);
const allSelected = ref<boolean>(false);

const props = defineProps<{
    article: ArticleModelOverlay;
    assetId?: string;
    productId?: string;
}>();

const emits = defineEmits<{
    (e: "onSaveSucceeded"): void;
    (e: "onClose"): void;
}>();

const { article, assetId, productId } = toRefs(props);

const { loading, collections, refetch } = useEditableCollections();
const { mutate: addArticlesMutation } = useAddArticlesToCollectionMutation({});
const { mutate: addMechanicArticlesMutation } = useAddMechanicsToCollectionMutation({});

watch(
    () => [article.value, productId.value],
    () => {
        selected.value = {};
        allSelected.value = false;
    }
);

const isLoading = computed(() => {
    return loading.value || saving.value;
});

const onCollectionSelect = (newSelected: Record<string, boolean>) => {
    selected.value = newSelected;
};

const addArticles = async (key: string, context: undefined | CollectionEntryNavigationContextInput) => {
    await addArticlesMutation(
        { collectionId: key, articleIds: [article.value.articleId!], context },
        {
            update: updateCollectionCache(key),
        }
    );
};

const addMechanicArticles = async (key: string, context: undefined | CollectionEntryMechanicNavigationContextInput) => {
    await addMechanicArticlesMutation(
        { collectionId: key, mechanicIds: [article.value.mechanicId!], context },
        {
            update: updateCollectionCache(key),
        }
    );
};

const onSave = async () => {
    const selectedCollections = Object.entries(selected.value);

    try {
        saving.value = true;
        let context: CollectionEntryNavigationContextInput | undefined = undefined;

        if (assetId.value || productId.value) {
            context = {};
            if (assetId.value) {
                context.assetId = assetId.value;
            }
            if (productId.value) {
                context.productId = productId.value;
            }
        }

        for (const [key] of selectedCollections) {
            if (article.value.mechanicId) {
                const mechanicContextInput: CollectionEntryMechanicNavigationContextInput = {
                    assetId: assetId.value,
                    productId: productId.value,
                    path: article.value.mechanicContext?.assemblyIdsPath,
                };
                await addMechanicArticles(key, mechanicContextInput);
            } else if (article.value.articleId) await addArticles(key, context);
        }

        allSelected.value = false;
        notify({
            message: t("collections.Added to collection successfully"),
            type: "positive",
        });
        emits("onSaveSucceeded");
    } catch (e: unknown) {
        notify({ message: e instanceof Error ? e.message : String(e) });
        console.error(e);
    } finally {
        selected.value = {};
        saving.value = false;
    }
};

const onCollectionCreated = async () => {
    await refetch();
};
</script>

<style lang="scss" scoped>
:deep(.card-small-mode:hover) {
    background: $white;
}
</style>
